// export default IncidentRequest;
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  Briefcase,
  Gavel,
  Smartphone,
  Monitor,
  Search,
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchComplaintByClientId,
  fetchComplaintReceptionByOrgAndAdminUnit,
} from "services/complaintService";
import {
  ComplaintReception,
  ComplaintStatus,
  ResolutionDecisionType,
  Roles,
} from "types";
import { ITEMS_PER_PAGE } from "utils/constant";
import { DateView, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";

// Tabs for display

const ComplaintList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  // Create tab arrays inside the component to ensure translations update
  const tab_for_all = [
    { key: "All", label: t("all") },
    { key: "pending", label: t("pending") },
    { key: "on_appointment", label: t("on_appointment") },
    { key: "resolved", label: t("resolved") },
    { key: "appealed", label: t("appealed") },
    { key: "mobile_request", label: t("mobile_request") },
  ];
  const tab_for_atorny = [
    { key: "All", label: t("all") },
    { key: "pending", label: t("pending") },
    { key: "on_appointment", label: t("on_appointment") },
    { key: "resolved", label: t("resolved") },
    { key: "appealed", label: t("appealed") },
    { key: "mobile_request", label: t("mobile_request") },
    { key: "search_by_client", label: t("search_by_application_number") },
  ];
  const tab_for_registrar = [
    { key: "All", label: t("all") },
    { key: "pending", label: t("pending") },
    { key: "registered", label: t("registered") },
  ];

  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [compliants, setCompliants] = useState<ComplaintReception[]>([]);
  const [compliantsByClientId, setCompliantsByClientId] = useState<
    ComplaintReception[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState(tab_for_all);

  const [clientId, setClientId] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const isCreator = hasRole(loginData?.roles, [Roles.complaintCreator]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);

  const fetchData = async () => {
    setLoading(true);
    const [complaintRes] = await Promise.all([
      fetchComplaintReceptionByOrgAndAdminUnit(
        loginData?.admin_unit ?? "",
        loginData?.org_unit ?? "",
        isRegistrar,
      ),
    ]);

    if (complaintRes) {
      const filteredData = isRegistrar
        ? complaintRes.filter((complaint) =>
            ["forwarded_to_registrar", "resolved"].includes(
              complaint.status ?? "",
            ),
          )
        : complaintRes;
      setCompliants(
        filteredData?.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ) || [],
      );
      setLoading(false);
    }
  };

  const fetchByClientId = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [complaintRes] = await Promise.all([
        fetchComplaintByClientId(clientId),
      ]);
      setCompliantsByClientId(Array.isArray(complaintRes) ? complaintRes : []);
    } catch (error: any) {
      setCompliantsByClientId([]);
      showTooltip(
        error?.response?.data?.message ||
          t("no_complaints_found_for_this_client_id"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loginData) {
      fetchData();
      if (
        loginData?.structure?.admin_unit &&
        loginData?.structure?.org_unit &&
        !loginData?.structure?.admin_unit?.parent_unit &&
        !loginData?.structure?.org_unit?.parent_unit
      ) {
        setTabs(tab_for_atorny);
      } else if (isRegistrar) {
        setTabs(tab_for_registrar);
      }
    }
  }, [activeTab, loginData]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = compliants.filter((complaint) => {
    // 1️⃣ — filter by status tab
    if (activeTab !== "All") {
      const tabKey = tabs.find((tab) => tab.label === activeTab)?.key;

      // Special handling for pending tab based on user role
      if (tabKey === "pending") {
        // For registrar role - show only forwarded_to_registrar status
        if (isRegistrar) {
          if (!["forwarded_to_registrar"].includes(complaint.status ?? "")) {
            return false;
          }
        }
        // For non-registrar roles - show pending and forwarded_to_management
        else {
          if (
            ![
              "pending",
              "forwarded_to_management",
              "forwarded_to_registrar",
            ].includes(complaint.status ?? "")
          ) {
            return false;
          }
        }
      }
      // Handle registered tab - filter by presence of registration_number
      else if (tabKey === "registered") {
        // Show only complaints that have a registration number
        if (!["resolved"].includes(complaint.status ?? "")) {
          return false;
        }
      } else if (tabKey === "appealed") {
        // Show only complaints that have a registration number
        if (!complaint?.is_appeal) {
          return false;
        }
      }
      // Handle mobile_request tab - filter by is_complaint_not_by_mobile flag
      else if (tabKey === "mobile_request") {
        // Show complaints that are NOT by mobile (is_complaint_not_by_mobile = true)
        if (complaint.is_complaint_not_by_mobile) {
          return false;
        }
      }
      // For other tabs (except "All" and "search_by_client"), check if status matches exactly
      else if (tabKey !== "search_by_client" && complaint.status !== tabKey) {
        return false;
      }
      // For "search_by_client" tab, we don't filter by status
    }

    // 2️⃣ — filter by search text
    const client_id = complaint?.client_id?.toString()?.toLowerCase() || "";
    const manager_name =
      complaint?.manager_name?.toString()?.toLowerCase() || "";
    const complaint_type =
      complaint?.complaint_type?.toString()?.toLowerCase() || "";
    const phone_number =
      complaint?.phone_number?.toString()?.toLowerCase() || "";
    const application_number =
      complaint?.application_number?.toString()?.toLowerCase() || "";

    const matchesText =
      client_id.includes(searchTerm) ||
      manager_name.includes(searchTerm) ||
      complaint_type.includes(searchTerm) ||
      phone_number.includes(searchTerm) ||
      application_number.includes(searchTerm);

    return matchesText;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate paginated incidents
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedData = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("complaint_request")}
        icon={Briefcase}
        tabs={tabs?.map((tb) => tb.label)}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={
          <>
            {isCreator && (
              <button
                onClick={() => navigate("register")}
                className="px-5 py-2 text-sm mt-8 font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                {t("create_new_complaint")}
              </button>
            )}
          </>
        }
      />

      {activeTab === t("search_by_application_number") && (
        <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
          <div className="overflow-hidden bg-white shadow rounded-xl">
            {/* add text field for client and and button called search when click show complaint info */}
            <div className="p-4 flex flex-row items-center gap-5">
              <input
                type="text"
                placeholder={t("enter_client_id")}
                className="border border-gray-300 rounded-md p-2 w-full"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
              <button
                className="px-4 py-2 bg-black text-white rounded-md"
                onClick={fetchByClientId}
              >
                {t("search")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_compliant")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={8} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      {/* Left: Back + Title */}
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span>{t("request_table")}</span>
                      </div>

                      {/* Right: Search Box */}
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          <Search className="w-4 h-4" />
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">{t("compliant_req")}</th>
                  <th className="p-3 text-left">{t("reception_date")}</th>
                  <th className="p-3 text-left">{t("phone_number")}</th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left">{t("application_number")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("view_action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === t("search_by_application_number")
                  ? compliantsByClientId
                  : paginatedData
                ).map((data, idx) => {
                  // find latest resolution by updated_at and get its status
                  const latestResolution = data.resolution_info?.sort(
                    (a: any, b: any) =>
                      new Date(a?.updated_at ?? 0) <
                      new Date(b?.updated_at ?? 0)
                        ? 1
                        : -1,
                  );
                  const decision_type =
                    latestResolution && latestResolution[0]?.decision_type;
                  return (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                    >
                      <td className="p-3 font-[500]  flex flex-row items-center gap-2">
                        <img
                          src="/complient.png"
                          alt="complient"
                          className="w-10 h-10"
                        />
                        {data?.client_id ?? t("n_a")}
                      </td>

                      <td className="p-3 font-[500] ">
                        {DateView(data.complaint_reception_date) || t("n_a")}
                      </td>

                      <td className="p-3 font-[500] ">
                        {data?.phone_number || t("n_a")}
                      </td>
                      <td className="p-3 font-[500] ">
                        <span className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
                          {data?.status && (
                            <span
                              className={`px-4 py-1 rounded-full text-sm font-bold ${
                                data.status === "resolved"
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : data.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                    : data.status === "closed"
                                      ? "bg-gray-200 text-gray-700 border border-gray-300"
                                      : data.status ===
                                          "forwarded_to_management"
                                        ? "bg-red-100 text-red-700 border border-red-300"
                                        : data.status === "under_review"
                                          ? "bg-purple-100 text-purple-700 border border-purple-300"
                                          : data.status === "appealed"
                                            ? "bg-red-100 text-red-700 border border-red-300"
                                            : "bg-gray-100 text-gray-700 border border-gray-300"
                              }
                             `}
                            >
                              {decision_type ===
                                "forward_to_higher_authority" ||
                              decision_type === "forward_to_lower_authority"
                                ? ResolutionDecisionType[decision_type]
                                : ComplaintStatus[data.status]}
                            </span>
                          )}
                        </span>{" "}
                      </td>
                      <td className="p-3 font-[500] ">
                        {data?.application_number || t("n_a")}
                      </td>
                      <td className="p-3 font-[500]">
                        <div className="flex flex-row items-center align-middle gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(data?.id + "/");
                            }}
                            className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                          >
                            {t("view")}
                          </button>
                          <div className="font-[500]">
                            {data?.is_appeal ? (
                              <Gavel className="w-5 h-5 text-red-600" />
                            ) : (
                              <Gavel className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                          <div className="font-[500]">
                            {data?.is_complaint_not_by_mobile ? (
                              <Monitor className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Smartphone className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(activeTab === t("search_by_application_number")
                  ? compliantsByClientId
                  : filtered
                ).length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-gray-500">
                      {t("no_compliant_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("prev")}
            </button>
            <span className="px-2">
              {t("page")} {currentPage} {t("of")} {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplaintList;
